import React from 'react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

export const Terms: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center">
            <h1 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}>
              Terms & Conditions
            </h1>
            <p className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}>
              Please read these terms and conditions carefully before using our safari booking platform.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className={`${mobile ? 'py-12' : tablet ? 'py-16' : 'py-20'} bg-background-primary`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-soft p-6 sm:p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary mb-8">
                <strong>Last updated:</strong> January 15, 2024
              </p>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  1. Acceptance of Terms
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  By accessing and using the Dala Rides platform ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  2. Description of Service
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Dala Rides is a safari booking platform that connects travelers with tour operators and transport providers in Tanzania. Our service includes:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Safari package listings and bookings</li>
                  <li>Transport service bookings</li>
                  <li>User account management</li>
                  <li>Payment processing</li>
                  <li>Customer support services</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  3. User Accounts and Registration
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  To access certain features of the Service, you must register for an account. You agree to:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your account information</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  4. Booking and Payment Terms
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  When making a booking through our platform:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>All prices are quoted in USD unless otherwise stated</li>
                  <li>A deposit is typically required to confirm your booking</li>
                  <li>Full payment is due 30-60 days before departure</li>
                  <li>Payment processing fees may apply</li>
                  <li>Bookings are subject to availability and operator confirmation</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  5. Cancellation and Refund Policy
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Cancellation policies vary by operator and package. General terms include:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Cancellations 60+ days before departure: Full refund minus processing fees</li>
                  <li>Cancellations 30-59 days before departure: 75% refund</li>
                  <li>Cancellations 15-29 days before departure: 50% refund</li>
                  <li>Cancellations less than 15 days: No refund</li>
                  <li>Force majeure events may result in full refunds or rescheduling</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  6. Operator Responsibilities
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Tour operators and transport providers listed on our platform must:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Maintain valid licenses and insurance</li>
                  <li>Provide accurate and up-to-date information</li>
                  <li>Honor confirmed bookings and reservations</li>
                  <li>Maintain safety standards and protocols</li>
                  <li>Respond to customer inquiries promptly</li>
                  <li>Comply with local regulations and laws</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  7. User Conduct and Responsibilities
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Users agree to:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Use the Service only for lawful purposes</li>
                  <li>Respect the rights and property of others</li>
                  <li>Follow safety instructions provided by operators</li>
                  <li>Carry necessary travel documents and insurance</li>
                  <li>Comply with local laws and customs</li>
                  <li>Not engage in fraudulent or deceptive practices</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  8. Limitation of Liability
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Dala Rides acts as an intermediary between travelers and service providers. We are not liable for:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Acts or omissions of third-party operators</li>
                  <li>Travel delays, cancellations, or itinerary changes</li>
                  <li>Personal injury or property damage during trips</li>
                  <li>Loss or theft of personal belongings</li>
                  <li>Weather conditions or natural disasters</li>
                  <li>Government actions or travel restrictions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  9. Privacy and Data Protection
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices regarding the collection and use of your personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  10. Intellectual Property
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  The Service and its original content, features, and functionality are owned by Dala Rides and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  11. Termination
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including without limitation if you breach the Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  12. Governing Law
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  These Terms shall be interpreted and governed by the laws of Tanzania, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  13. Changes to Terms
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  14. Contact Information
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  If you have any questions about these Terms and Conditions, please contact us at:
                </p>
                <div className="bg-background-secondary rounded-xl p-4 mt-4">
                  <p className="text-text-secondary">
                    <strong>Email:</strong> legal@dalarides.com<br />
                    <strong>Phone:</strong> +255 123 456 789<br />
                    <strong>Address:</strong> 123 Safari Street, Arusha, Tanzania
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}; 